import { type ApolloClient } from "@apollo/client";
import { NonIdealState, Spinner } from "@blueprintjs/core";
import { useGetDeviceQuery } from "@package/ui-gql/index-data-access";
import {
  type ReduxWallState,
  type WallProvider,
} from "@package/ui-wall-engine";
import { WallProviderLocal } from "@package/ui-wall-engine/src/interface/wall-provider-local";
import { WallProviderRemote } from "@package/ui-wall-engine/src/interface/wall-provider-remote";
import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { type Store, type UnknownAction } from "redux";
import { useWallConfig } from "../context-wall-config/context-wall-config";
import { WallContext } from "./context-wall";

type WallProviderProps = {
  clientDataAccess: ApolloClient<object>;
  clientAnalytics?: ApolloClient<object>;
  device?: string;
  location?: string;
  profile?: string;
  store: Store<ReduxWallState, UnknownAction>;
  type: "local" | "remote";
  caching?: boolean;
};

export function WallContextProvider({
  clientDataAccess,
  clientAnalytics,
  device,
  location,
  profile,
  store,
  type,
  caching = true,
  children,
}: PropsWithChildren<WallProviderProps>) {
  const [provider, setProvider] = useState<WallProvider>();
  const { update } = useWallConfig();

  const {
    data: dataDevice,
    loading: loadingDevice,
    error: errorDevice,
  } = useGetDeviceQuery({
    skip: !device || !!location,
    variables: {
      id: device ?? "",
    },
  });

  const locationSelected = useMemo(
    () => location ?? dataDevice?.item?.location ?? undefined,
    [dataDevice?.item?.location, location],
  );

  useEffect(
    function effectOnCreateWallProvider() {
      let p: WallProvider | undefined;

      if (locationSelected || profile) {
        p =
          type === "local"
            ? new WallProviderLocal(
                clientDataAccess,
                clientAnalytics,
                store,
                locationSelected,
                device,
                profile,
                caching,
              )
            : new WallProviderRemote();

        p.on("profile-updated", (profile) => {
          if (profile) {
            update((draft) => {
              draft.profile = profile;
            });
          }
        });

        setProvider(p);
        console.info("[wall-context-provider]: Wall provider created");
      } else {
        console.warn(
          "[wall-context-provider]: No location or profile provided",
        );
      }

      return () => {
        if (p) {
          console.info("[wall-context-provider]: Destroying wall provider");
          p.destroy();
        }
      };
    },
    [
      caching,
      clientAnalytics,
      clientDataAccess,
      device,
      locationSelected,
      profile,
      store,
      type,
      update,
    ],
  );

  if (device && !location) {
    if (loadingDevice) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <Spinner size={100} />
        </div>
      );
    }

    if (errorDevice || !dataDevice?.item?.location) {
      return (
        <div className="flex flex-col items-center justify-center h-full w-full">
          <NonIdealState title="Failed loading wall device" icon="error" />
        </div>
      );
    }
  }

  return (
    <WallContext.Provider value={provider}>{children}</WallContext.Provider>
  );
}
